import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Tag, Upload, Button, Space, Segmented, Image, Typography, message, Alert, Select, Modal, Popconfirm } from 'antd';
import { UploadOutlined, PictureOutlined, CloudUploadOutlined, UndoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { DEITIES, festivalImage } from '../utils/festivalImages';

const { Text } = Typography;

const norm = (s) => String(s || '').toLowerCase().replace(/\.[a-z0-9]+$/, '').replace(/[^a-z0-9]/g, '');
const ALIASES = { deepawali: 'diwali', deepavali: 'diwali', rakhi: 'rakshabandhan', janmashtami: 'krishnajanmashtami', shivratri: 'mahashivratri', navratri: 'navratrishuru', ganeshchaturthi: 'ganeshchaturthi', chhath: 'chhathpuja', govardhan: 'govardhanpuja', dhanteras: 'dhanteras', bhaidooj: 'bhaidooj', holikadahan: 'holikadahan' };

function festivalYear(r) {
  return (r.next_date || r.festival_date || '').slice(0, 4);
}

export function uploadFestivalPhoto(id, file) {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/festivals/${id}/photo`, fd);
}

export default function FestivalPhotos() {
  const [rows, setRows] = useState([]);
  const [deityPhotos, setDeityPhotos] = useState({});
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(String(dayjs().year()));
  const [bulk, setBulk] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/festivals'), api.get('/festivals/deity-photos')])
      .then(([f, d]) => { setRows(Array.isArray(f.data) ? f.data : []); setDeityPhotos(d.data || {}); })
      .catch(() => message.error('Festival list load nahi hui'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const years = useMemo(() => {
    const ys = new Set([String(dayjs().year()), String(dayjs().year() + 1)]);
    rows.forEach(r => { const y = festivalYear(r); if (y) ys.add(y); });
    return [...ys].sort();
  }, [rows]);

  const list = useMemo(() => rows
    .filter(r => Number(r.is_active) !== 0)
    .map(r => (r.month_day && !r.festival_date) ? { ...r, show_date: `${year}-${r.month_day}` } : { ...r, show_date: r.festival_date })
    .filter(r => (r.show_date || '').startsWith(year))
    .sort((a, b) => a.show_date.localeCompare(b.show_date)), [rows, year]);

  const doUpload = async (id, file) => {
    try {
      await uploadFestivalPhoto(id, file);
      message.success('Photo save ho gayi');
      load();
    } catch (e) {
      message.error(e.response?.data?.error || 'Photo upload nahi hui');
    }
    return false;
  };

  const resetPhoto = async (id) => {
    try {
      await api.put(`/festivals/${id}`, { image_path: null });
      message.success('Ab default photo dikhegi');
      load();
    } catch (e) { message.error(e.response?.data?.error || 'Nahi hua'); }
  };

  const uploadDeity = async (deity, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/festivals/deity-photo/${deity}`, fd);
      message.success('Default photo save ho gayi');
      load();
    } catch (e) { message.error(e.response?.data?.error || 'Photo upload nahi hui'); }
    return false;
  };

  const matchFestival = (fileName) => {
    const n = norm(fileName);
    const yr = (fileName.match(/20\d\d/) || [])[0] || year;
    const key = n.replace(/20\d\d/g, '');
    const target = ALIASES[key] || key;
    const pool = rows.filter(r => Number(r.is_active) !== 0 && (r.festival_date ? r.festival_date.startsWith(yr) : true));
    return (pool.find(r => norm(r.name) === target) || pool.find(r => target && (norm(r.name).includes(target) || target.includes(norm(r.name)))))?.id || null;
  };

  const onBulkPick = (file, fileList) => {
    if (file === fileList[fileList.length - 1]) {
      setBulk(fileList.map(f => ({ file: f, name: f.name, festival_id: matchFestival(f.name), url: URL.createObjectURL(f) })));
      setBulkOpen(true);
    }
    return false;
  };

  const saveBulk = async () => {
    setBulkSaving(true);
    let ok = 0, fail = 0;
    for (const b of bulk) {
      if (!b.festival_id) continue;
      try { await uploadFestivalPhoto(b.festival_id, b.file); ok++; } catch { fail++; }
    }
    setBulkSaving(false);
    setBulkOpen(false);
    setBulk([]);
    message.success(`${ok} photo lag gayi${fail ? `, ${fail} fail` : ''}`);
    load();
  };

  const festOptions = rows.filter(r => Number(r.is_active) !== 0)
    .map(r => ({ value: r.id, label: `${r.name} — ${r.festival_date ? dayjs(r.festival_date).format('DD MMM YYYY') : 'har saal ' + r.month_day}` }));

  const columns = [
    { title: 'Date', dataIndex: 'show_date', width: 150, render: (d) => {
      const left = dayjs(d).startOf('day').diff(dayjs().startOf('day'), 'day');
      return <div><b>{dayjs(d).format('DD MMM YYYY')}</b><br /><Text type="secondary">{dayjs(d).format('dddd')}</Text>
        {left >= 0 && left <= 2 && <div><Tag color="red">{left === 0 ? 'Aaj' : `${left} din baad`}</Tag></div>}
        {left < 0 && <div><Tag>Nikal gaya</Tag></div>}</div>;
    } },
    { title: 'Tyohar', dataIndex: 'name', render: (n, r) => <div><b>{n}</b><br /><Text type="secondary">{r.greeting}</Text>{r.month_day && !r.festival_date && <div><Tag color="blue">Har saal</Tag></div>}</div> },
    { title: 'Photo', width: 130, render: (_, r) => <Image src={festivalImage(r.image, r.deity)} width={96} height={64} style={{ objectFit: 'cover', borderRadius: 8 }} /> },
    { title: 'Status', width: 130, render: (_, r) => r.image_path ? <Tag color="green">Apni photo lagi</Tag> : <Tag color="orange">Default photo</Tag> },
    { title: 'Action', width: 220, render: (_, r) => <Space wrap>
      <Upload accept="image/jpeg,image/png,image/webp" showUploadList={false} beforeUpload={(f) => doUpload(r.id, f)}>
        <Button type="primary" icon={<UploadOutlined />}>Photo Upload</Button>
      </Upload>
      {r.image_path && <Popconfirm title="Default photo wapas lagayein? (Aapki photo server par safe rahegi)" onConfirm={() => resetPhoto(r.id)}>
        <Button icon={<UndoOutlined />}>Default</Button></Popconfirm>}
    </Space> },
  ];

  return (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="Poore saal ke tyohar yahan hain. Har tyohar ki photo pehle se upload kar do, wahi tyohar wale din popup me aayegi."
        description="Tyohar se 2 din pehle admin ko dashboard par yaad dilaya jayega agar photo nahi lagi. Ek saath kai photo daalni ho to file ka naam tyohar ke naam par rakho (jaise diwali.jpg, holi 2027.jpg). Ekadashi ki photo Krishna ji wali default photo se aati hai." />
      <Card style={{ marginBottom: 16 }} title={<Space><PictureOutlined /> Festival Calendar</Space>}
        extra={<Space wrap>
          <Segmented value={year} onChange={setYear} options={years} />
          <Upload multiple accept="image/jpeg,image/png,image/webp" showUploadList={false} beforeUpload={onBulkPick}>
            <Button icon={<CloudUploadOutlined />} style={{ background: '#722ed1', color: '#fff' }}>Ek saath kai photo upload</Button>
          </Upload>
        </Space>}>
        <Table rowKey="id" loading={loading} dataSource={list} columns={columns} pagination={false} size="middle" scroll={{ x: 760 }}
          locale={{ emptyText: `${year} ke tyohar abhi list me nahi hain` }} />
      </Card>

      <Card title="Devta ki default photo (jin tyoharon ki apni photo nahi lagi, unme ye aayegi)">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 14 }}>
          {DEITIES.map(d => (
            <div key={d.value} style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: 8, textAlign: 'center' }}>
              <Image src={festivalImage(deityPhotos[d.value], d.value)} width="100%" height={100} style={{ objectFit: 'cover', borderRadius: 8 }} />
              <div style={{ margin: '6px 0', fontWeight: 600 }}>{d.label}</div>
              <Upload accept="image/jpeg,image/png,image/webp" showUploadList={false} beforeUpload={(f) => uploadDeity(d.value, f)}>
                <Button size="small" icon={<UploadOutlined />}>Badlo</Button>
              </Upload>
            </div>
          ))}
        </div>
      </Card>

      <Modal open={bulkOpen} title="Photo kis tyohar ki hai — check karo" width={720} onCancel={() => { setBulkOpen(false); setBulk([]); }}
        onOk={saveBulk} okText={`Save (${bulk.filter(b => b.festival_id).length})`} confirmLoading={bulkSaving}>
        <Text type="secondary">Naam se tyohar apne aap chun liya gaya hai. Galat ho ya khali ho to yahan se chuno.</Text>
        {bulk.map((b, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
            <img src={b.url} alt="" style={{ width: 70, height: 48, objectFit: 'cover', borderRadius: 6 }} />
            <div style={{ width: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
            <Select showSearch optionFilterProp="label" style={{ flex: 1 }} placeholder="Tyohar chuno" value={b.festival_id}
              options={festOptions} onChange={(v) => setBulk(bulk.map((x, j) => j === i ? { ...x, festival_id: v } : x))} />
          </div>
        ))}
      </Modal>
    </div>
  );
}
